import React, { useCallback, useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import DocumentViewerPanel from "@/components/shared/DocumentViewerPanel";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { formatCurrency } from "@/utils/paymentUtils";
import { Loader2, CheckCircle2, AlertTriangle, FileX, RefreshCw } from "lucide-react";
import { useFormatters } from "@financialharmony/ui";
import type { Invoice } from "@/types/payments";

interface RejectedDocumentCompareDialogProps {
  documentId: string | null;
  companyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const normalizeStr = (value?: string | null) => (value ?? "").trim().toLowerCase();
const normalizeDate = (value?: string | null) =>
  value && value.trim() ? new Date(value).toDateString() : "";
type CompareFieldKey = "vendor" | "amount" | "date" | "reference";

interface CompareField {
  key: CompareFieldKey;
  matches: boolean;
  rejectedValue: string;
  originalValue: string;
}

function buildCompareFields(
  rejected: Invoice,
  original: Invoice,
  formatDate: (value?: string | null) => string,
): CompareField[] {
  return [
    {
      key: "vendor",
      matches: normalizeStr(rejected.vendor) === normalizeStr(original.vendor),
      rejectedValue: rejected.vendor || "—",
      originalValue: original.vendor || "—",
    },
    {
      key: "amount",
      matches: rejected.amount === original.amount,
      rejectedValue: formatCurrency(rejected.amount, rejected.currency),
      originalValue: formatCurrency(original.amount, original.currency),
    },
    {
      key: "date",
      matches: normalizeDate(rejected.dueDate) === normalizeDate(original.dueDate),
      rejectedValue: formatDate(rejected.dueDate),
      originalValue: formatDate(original.dueDate),
    },
    {
      key: "reference",
      matches:
        normalizeStr(rejected.remittanceInformation) === normalizeStr(original.remittanceInformation),
      rejectedValue: rejected.remittanceInformation || "—",
      originalValue: original.remittanceInformation || "—",
    },
  ];
}

const RejectedDocumentCompareDialog: React.FC<RejectedDocumentCompareDialogProps> = ({
  documentId,
  companyId,
  open,
  onOpenChange,
}) => {
  const t = useTranslations("consultantUpload.compare");
  const { formatDate: formatDateValue } = useFormatters();
  const [loading, setLoading] = useState(false);
  const [rejected, setRejected] = useState<Invoice | null>(null);
  const [original, setOriginal] = useState<Invoice | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchCompare = useCallback(() => {
    if (!documentId) return;
    setLoading(true);
    setLoadFailed(false);
    ServiceRegistry.getInvoiceUploadService()
      .getRejectedDocumentCompare(documentId, companyId)
      .then((result) => {
        setRejected(result.rejected);
        setOriginal(result.original);
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, [documentId, companyId]);

  useEffect(() => {
    if (!open || !documentId) return;
    fetchCompare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentId]);

  if (!open) return null;

  const formatDate = (value?: string | null) => (value && value.trim() ? formatDateValue(value, "numeric") : "—");
  const compareFields = rejected && original ? buildCompareFields(rejected, original, formatDate) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
          <DialogDescription>
            {t("subtitle", { file: rejected?.fileName || t("rejectedDocument") })}
          </DialogDescription>
        </DialogHeader>

        {loadFailed ? (
          <div
            data-testid="compare-load-failed"
            className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground"
          >
            <FileX className="h-8 w-8" />
            <p className="text-sm text-center">
              {t("loadFailed")}
            </p>
            <Button variant="outline" size="sm" onClick={fetchCompare}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {t("retry")}
            </Button>
          </div>
        ) : loading || !rejected ? (
          <div
            data-testid="compare-loading"
            className="flex flex-1 items-center justify-center text-muted-foreground"
          >
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <ResizablePanelGroup direction="horizontal" className="flex-1 mt-4">
            <ResizablePanel defaultSize={50} minSize={25}>
              {rejected.filePath ? (
                <DocumentViewerPanel filePath={rejected.filePath} fileName={rejected.fileName ?? undefined} />
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <p className="text-sm">{t("noFile")}</p>
                </div>
              )}
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={50} minSize={30} className="pl-4">
              <div className="h-full overflow-y-auto pb-4 space-y-4">
                {original ? (
                  <>
                    {original.filePath && (
                      <div className="h-64">
                        <DocumentViewerPanel filePath={original.filePath} fileName={original.fileName ?? undefined} />
                      </div>
                    )}
                    <div className="space-y-3">
                      {compareFields.map((field) => (
                        <div key={field.key} className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                          {field.matches ? (
                            <CheckCircle2
                              data-testid="field-match-icon"
                              className="h-4 w-4 text-green-600 mt-0.5 shrink-0"
                            />
                          ) : (
                            <AlertTriangle
                              data-testid="field-mismatch-icon"
                              className="h-4 w-4 text-amber-600 mt-0.5 shrink-0"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-muted-foreground">{t(`fields.${field.key}`)}</p>
                            <p className="text-sm font-medium">
                              {field.rejectedValue}
                              {!field.matches && (
                                <span className="text-muted-foreground font-normal">
                                  {" "}
                                  {t("originalValue", { value: field.originalValue })}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div
                    data-testid="original-unavailable"
                    className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground"
                  >
                    <FileX className="h-8 w-8" />
                    <p className="text-sm text-center">
                      {t("originalUnavailable")}
                    </p>
                    <Button variant="outline" size="sm" onClick={fetchCompare}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      {t("retry")}
                    </Button>
                  </div>
                )}
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RejectedDocumentCompareDialog;
